import { useEffect, useState, useRef } from "react";
import { BRAND } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Preshh Arts — editorial visual showcase of the world surrounding Preshh.
 * Auto-rotating carousel with pause on hover / interaction, keyboard arrows and swipe.
 * Photographs are shown as supplied — object-contain preserves original framing.
 * Each photo has an editorial caption and alt text for accessibility & SEO
 * (Open Graph image, schema.org ImageGallery JSON-LD).
 */
export default function Arts() {
  const images = BRAND.arts || [];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);
  const current = images[i] || {};

  useEffect(() => {
    if (paused || images.length < 2) return;
    const t = setTimeout(() => setI((n) => (n + 1) % images.length), 5500);
    return () => clearTimeout(t);
  }, [i, paused, images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setI((n) => (n + 1) % images.length);
      if (e.key === "ArrowLeft") setI((n) => (n - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length]);

  // Dynamic <head> tags for SEO + social sharing
  useEffect(() => {
    document.title = "Preshh Arts — The world around Preshh";
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "An editorial gallery of the craft, bartenders, cocktails and ingredients that surround the Preshh carbonation system.");
    setMeta("og:title", "Preshh Arts — Behind the bubbles", true);
    setMeta("og:description", "An editorial gallery of the craft that grows around every Preshh carbonation cycle.", true);
    setMeta("og:type", "website", true);
    if (current.url) setMeta("og:image", current.url, true);
    setMeta("twitter:card", "summary_large_image");
    if (current.caption) setMeta("twitter:description", current.caption);

    // JSON-LD ImageGallery — richer results & image indexing
    const ldId = "preshh-arts-jsonld";
    let ld = document.getElementById(ldId);
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = ldId; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "name": "Preshh Arts",
      "description": "Editorial photography of the Preshh carbonation craft — bartenders, cocktails, sodas and equipment.",
      "image": images.map((img) => ({
        "@type": "ImageObject",
        "contentUrl": img.url,
        "caption": img.caption,
        "description": img.alt || img.caption,
        "creditText": "Preshh",
        "copyrightNotice": "© Preshh",
      })),
    });
    return () => { if (ld) ld.remove(); };
  }, [current.url, current.caption, images]);

  const next = () => { setPaused(true); setI((n) => (n + 1) % images.length); };
  const prev = () => { setPaused(true); setI((n) => (n - 1 + images.length) % images.length); };
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStart.current = null;
  };

  return (
    <div className="text-white bg-[#050505]" data-testid="arts-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-6">
        <p className="uppercase text-xs tracking-[0.3em] text-[#00B5D8] mb-3">Preshh Arts</p>
        <h1 className="font-display text-5xl md:text-6xl leading-[0.95]">The world around Preshh.</h1>
        <p className="mt-5 text-white/60 max-w-xl leading-relaxed">
          Cocktails, bartenders, bars, ingredients — an editorial look at the craft that grows around every carbonation cycle.
        </p>
      </section>

      <section
        className="relative bg-black"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Immersive frame — takes nearly the full viewport height so the photograph is the hero */}
        <div className="relative h-[72vh] md:h-[86vh] min-h-[440px] overflow-hidden">
          {images.map((img, idx) => (
            <figure
              key={img.url}
              className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${idx === i ? "opacity-100" : "opacity-0"}`}
              aria-hidden={idx !== i}
            >
              <img
                src={img.url}
                alt={img.alt || img.caption}
                className="w-full h-full object-contain"
                loading={idx === 0 ? "eager" : "lazy"}
              />
            </figure>
          ))}

          {/* Minimal navigation — no chrome, just the mark */}
          <button data-testid="arts-prev" onClick={prev} aria-label="Previous image"
            className="group absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/50 hover:text-white transition-colors">
            <span className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/5 transition-colors" />
            <ChevronLeft size={26} strokeWidth={1.25} className="relative" />
          </button>
          <button data-testid="arts-next" onClick={next} aria-label="Next image"
            className="group absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/50 hover:text-white transition-colors">
            <span className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/5 transition-colors" />
            <ChevronRight size={26} strokeWidth={1.25} className="relative" />
          </button>
        </div>

        {/* Caption bar — anchored below the frame; premium restraint */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 text-center bg-black">
          <p key={current.url} data-testid="arts-caption" className="text-white/85 italic text-base md:text-lg leading-relaxed animate-[fadeIn_800ms_ease-out]">
            {current.caption}
          </p>
          <p className="text-[11px] tracking-[0.24em] uppercase text-white/35 mt-4">{String(i + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</p>
        </div>

        <div className="flex justify-center gap-1.5 pb-6 bg-black">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setPaused(true); setI(idx); }}
              aria-label={`Go to image ${idx + 1}`}
              data-testid={`arts-dot-${idx}`}
              className={`h-[2px] rounded-full transition-all duration-500 ${idx === i ? "w-8 bg-[#00B5D8]" : "w-3 bg-white/20 hover:bg-white/40"}`}
            />
          ))}
        </div>
      </section>

      {/* Editorial thumbnail grid with captions */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img, idx) => (
            <button
              key={img.url}
              onClick={() => { setI(idx); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className={`group text-left rounded-2xl overflow-hidden bg-[#0a0a0a] border transition-colors ${idx === i ? "border-[#00B5D8]" : "border-white/10 hover:border-white/25"}`}
              data-testid={`arts-thumb-${idx}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img src={img.url} alt={img.alt || img.caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <figcaption className="p-4 text-xs text-white/60 leading-relaxed line-clamp-2">{img.caption}</figcaption>
            </button>
          ))}
        </div>
      </section>

      {/* CSS keyframe for caption fade */}
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}
